import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loading, EmptyArt, SearchBox, ReasonDialog, cardCls, blueBtn } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg, fmtDateTime } from "@/lib/api";
import { activeItems, billPayload, comandaTitle, filterComandas } from "@/lib/mobile";
import { printDoc, printErr } from "@/lib/btprint";
import { cn } from "@/lib/utils";

function Detail({ c, onClose }) {
  const { session } = useAuth();
  const [reopen, setReopen] = useState(false);
  const nav = useNavigate();
  const reprint = async () => {
    try {
      if (c.sale_id) {
        const { data } = await api.get(`/mobile/sales/${c.sale_id}`);
        await printDoc(session, "sale", `2ª via · Venda #${data.number} · ${comandaTitle(c)}`, { sale: data });
      } else await printDoc(session, "bill", `Conta · ${comandaTitle(c)}`, billPayload(c));
    } catch (e) { toast.error(printErr(e)); }
  };
  const doReopen = async (reason) => {
    try { await api.post(`/comandas/${c.id}/reopen`, { reason }); toast.success("Comanda reaberta"); nav(`/m/comanda/${c.id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-sm overflow-y-auto" data-testid="m-closed-dialog">
        <DialogHeader><DialogTitle>{comandaTitle(c)}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{c.customer_name || "Sem nome"} · fechada {fmtDateTime(c.closed_at)}{c.closed_by ? ` por ${c.closed_by}` : ""}</p>
        <div className="space-y-1 text-sm">
          {activeItems(c).map((i) => <div key={i.id} className="flex justify-between"><span>{i.qty}x {i.name}</span><span className="font-mono">{brl(i.total)}</span></div>)}
          <div className="flex justify-between border-t pt-2 font-bold"><span>Total</span><span className="font-mono">{brl(c.total)}</span></div>
        </div>
        <Button variant="outline" onClick={reprint} data-testid="m-closed-reprint-btn"><Printer className="mr-2 h-4 w-4" />Reimprimir</Button>
        {canDo(session.user, "reopen_comanda") && <Button className={blueBtn} onClick={() => setReopen(true)} data-testid="m-closed-reopen-btn"><RotateCcw className="mr-2 h-4 w-4" />Reabrir comanda</Button>}
        <ReasonDialog open={reopen} title={`Reabrir a comanda #${c.number}?`} description="A venda do fechamento é estornada e a comanda volta a ficar aberta." required confirmText="Reabrir" onClose={() => setReopen(false)} onConfirm={doReopen} testId="m-reopen" />
      </DialogContent>
    </Dialog>
  );
}

export function ClosedComandas() {
  const { data } = usePoll(() => api.get("/comandas?status=closed").then((r) => r.data), 0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  if (!data) return <Loading />;
  const list = filterComandas(data, q).sort((a, b) => (b.closed_at || "").localeCompare(a.closed_at || ""));
  return (
    <div className="space-y-3">
      <SearchBox value={q} onChange={setQ} placeholder="Buscar por número ou nome" testId="m-closed-search" />
      {!list.length ? <EmptyArt icon={Archive} title="Nenhuma comanda fechada" testId="m-closed-empty" /> : (
        <ul className="space-y-2" data-testid="m-closed-list">
          {list.map((c) => (
            <li key={c.id}>
              <button onClick={() => setOpen(c)} className={cn(cardCls, "flex w-full items-center gap-3 p-3 text-left")} data-testid={`m-closed-${c.number}-${c.id.slice(0, 6)}`}>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-slate-200 font-mono text-sm font-bold">#{c.number}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{c.customer_name || (c.table_number ? `Mesa ${c.table_number}` : "Sem nome")}</span>
                  <span className="text-xs text-slate-500">{fmtDateTime(c.closed_at)}</span></span>
                <span className="font-mono font-bold">{brl(c.total)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && <Detail c={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
